import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Send,
  User,
  Headphones,
  Loader2,
  UserCheck,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { Header } from '@/components/layout/Header';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useTicketAdmin } from '@/hooks/useTicketAdmin';
import {
  ADMIN_TICKET_STATUSES,
  ticketSourceLabel,
  ticketStatusConfig,
} from '@/lib/ticketStatus';

interface TicketMessage {
  id: string;
  ticket_id: string;
  sender_id: string;
  sender_type: 'user' | 'admin' | 'system';
  body: string;
  is_read: boolean;
  created_at: string;
}

interface AdminTicket {
  id: string;
  ticket_number: string;
  subject: string;
  status: string;
  created_at: string;
  description: string | null;
  metadata: Record<string, unknown> | null;
  reporter_name: string | null;
  assigned_to: string | null;
}

const MAX_MESSAGE_LENGTH = 5000;

/**
 * AdminTicketChatPage — admin-vyn för ett enskilt ärende.
 *
 * Samma struktur som ärendechatten i pwa-handel (header-sektion med
 * tillbaka/ämne/status, meddelandebubblor och fast svarsfält längst ner)
 * men från supportsidan: adminsvar skrivs som sender_type 'admin' (samma
 * mönster som admin-konsolen, så kundens app visar dem som support),
 * status kan bytas och ärendet kan tilldelas den inloggade
 * administratören. Svarsfältet är anpassat till åtkomstappens flytande
 * bottenmeny. RLS på tickets/ticket_messages är behörighetsgränsen;
 * rollchecken är UX.
 */
export default function AdminTicketChatPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isTicketAdmin, isLoading: roleLoading } = useTicketAdmin();
  const [message, setMessage] = useState('');
  const queryClient = useQueryClient();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!roleLoading && !isTicketAdmin) navigate('/', { replace: true });
  }, [roleLoading, isTicketAdmin, navigate]);

  const { data: ticket, isLoading: ticketLoading } = useQuery({
    queryKey: ['admin-ticket', ticketId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tickets')
        .select(
          'id, ticket_number, subject, status, created_at, description, metadata, reporter_name, assigned_to'
        )
        .eq('id', ticketId)
        .single();

      if (error) throw error;
      return data as AdminTicket;
    },
    enabled: !!ticketId && isTicketAdmin,
  });

  const { data: messages, isLoading: messagesLoading } = useQuery({
    queryKey: ['admin-ticket-messages', ticketId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ticket_messages')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data as TicketMessage[];
    },
    enabled: !!ticketId && isTicketAdmin,
  });

  // Markera kundens olästa meddelanden som lästa (speglar kundvyns logik).
  useEffect(() => {
    const markMessagesAsRead = async () => {
      if (!messages || !user) return;

      const unreadUserMessages = messages.filter((m) => m.sender_type === 'user' && !m.is_read);
      if (unreadUserMessages.length === 0) return;

      const { error } = await supabase
        .from('ticket_messages')
        .update({ is_read: true })
        .in('id', unreadUserMessages.map((m) => m.id));

      if (!error) {
        queryClient.invalidateQueries({ queryKey: ['admin-ticket-messages', ticketId] });
      }
    };

    markMessagesAsRead();
  }, [messages, user, queryClient, ticketId]);

  // Realtime: nya meddelanden i ärendet (samma mönster som kundchatten).
  useEffect(() => {
    if (!ticketId) return;

    const channel = supabase
      .channel(`admin-ticket-messages-${ticketId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'ticket_messages',
          filter: `ticket_id=eq.${ticketId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['admin-ticket-messages', ticketId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [ticketId, queryClient]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Adminsvar — direkt insert med sender_type 'admin' (samma mönster som
  // admin-konsolen); RLS-policyn "Admins can send messages on org tickets"
  // är behörighetsgränsen.
  const sendMutation = useMutation({
    mutationFn: async (body: string) => {
      const { data, error } = await supabase
        .from('ticket_messages')
        .insert({
          ticket_id: ticketId,
          sender_id: user!.id,
          sender_type: 'admin',
          body,
          is_read: false,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-ticket-messages', ticketId] });
      setMessage('');
    },
    onError: () => {
      toast.error('Kunde inte skicka meddelande');
    },
  });

  const updateTicket = useMutation({
    mutationFn: async (fields: Partial<Pick<AdminTicket, 'status' | 'assigned_to'>>) => {
      const { error } = await supabase.from('tickets').update(fields).eq('id', ticketId!);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-ticket', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['admin-tickets'] });
      toast.success('Ärendet uppdaterat');
    },
    onError: () => {
      toast.error('Kunde inte uppdatera ärendet');
    },
  });

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    const body = message.trim();
    if (!body) return;
    if (body.length > MAX_MESSAGE_LENGTH) {
      toast.error(`Meddelandet är för långt (max ${MAX_MESSAGE_LENGTH} tecken)`);
      return;
    }
    sendMutation.mutate(body);
  };

  if (roleLoading || ticketLoading || !isTicketAdmin) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="flex flex-1 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </main>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="mx-auto w-full max-w-md flex-1 px-4 py-6 text-center">
          <p className="text-muted-foreground">Ärendet hittades inte</p>
          <Button onClick={() => navigate('/admin/arenden')} className="mt-4">
            Tillbaka till ärenden
          </Button>
        </main>
      </div>
    );
  }

  const statusCfg = ticketStatusConfig(ticket.status);
  const source = ticketSourceLabel(ticket.metadata);
  const isAssignedToMe = !!user && ticket.assigned_to === user.id;

  return (
    <div className="flex min-h-screen flex-col">
      <Header />

      {/* Ärendehuvud med status- och tilldelningskontroller */}
      <div className="border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="mx-auto max-w-md space-y-3 px-4 py-3">
          <button
            onClick={() => navigate('/admin/arenden')}
            className="flex items-center gap-2 text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Tillbaka</span>
          </button>

          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h1 className="truncate font-semibold text-foreground">{ticket.subject}</h1>
              <p className="text-xs text-muted-foreground">
                #{ticket.ticket_number}
                {ticket.reporter_name && ` · ${ticket.reporter_name}`}
                {source && ` · ${source}`}
              </p>
            </div>
            <Badge className={`shrink-0 ${statusCfg.badgeClass}`}>{statusCfg.label}</Badge>
          </div>

          {/* Statusbyte */}
          <div className="flex gap-2 overflow-x-auto scrollbar-none -mx-4 px-4 py-0.5">
            {ADMIN_TICKET_STATUSES.map((s) => {
              const isActive = ticket.status === s.value;
              return (
                <button
                  key={s.value}
                  disabled={isActive || updateTicket.isPending}
                  onClick={() => updateTicket.mutate({ status: s.value })}
                  className={`shrink-0 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                    isActive
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-card/50 text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {s.label}
                </button>
              );
            })}
          </div>

          {/* Tilldelning */}
          {isAssignedToMe ? (
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <UserCheck className="h-3.5 w-3.5" />
              Tilldelat dig
            </p>
          ) : (
            <Button
              variant="outline"
              size="sm"
              disabled={updateTicket.isPending}
              onClick={() =>
                updateTicket.mutate({
                  assigned_to: user!.id,
                  // Samma semantik som api-tickets /assign: tilldelning öppnar ärendet.
                  ...(ticket.status === 'new' ? { status: 'open' } : {}),
                })
              }
              className="gap-1.5"
            >
              <UserCheck className="h-4 w-4" />
              Tilldela mig
            </Button>
          )}
        </div>
      </div>

      {/* Meddelanden — extra bottenluft så fasta svarsfältet inte skymmer */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-md space-y-3 px-4 py-4 pb-24">
          {/* Ursprunglig rapport — visas alltid först, som i kundchatten */}
          {ticket.description && (
            <Card className="border-border bg-card/50 p-4 backdrop-blur-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <FileText className="h-3.5 w-3.5 shrink-0" />
                <p className="text-xs font-medium">
                  Ärendebeskrivning ·{' '}
                  {format(new Date(ticket.created_at), 'd MMM HH:mm', { locale: sv })}
                </p>
              </div>
              <p className="mt-2 whitespace-pre-wrap text-sm text-foreground">
                {ticket.description}
              </p>
            </Card>
          )}

          {messagesLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : messages?.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-sm text-muted-foreground">
                Inga meddelanden ännu. Svara kunden nedan.
              </p>
            </div>
          ) : (
            messages?.map((msg) => {
              // Admin-vyn: supportens svar till höger, kundens till vänster.
              const isOwn = msg.sender_type !== 'user';
              return (
                <div key={msg.id} className={`flex gap-2 ${isOwn ? 'flex-row-reverse' : ''}`}>
                  <div
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                      isOwn ? 'bg-primary/20' : 'bg-accent'
                    }`}
                  >
                    {isOwn ? (
                      <Headphones className="h-4 w-4 text-primary" />
                    ) : (
                      <User className="h-4 w-4 text-accent-foreground" />
                    )}
                  </div>
                  <div className={`max-w-[80%] flex-1 ${isOwn ? 'text-right' : ''}`}>
                    <Card
                      className={`inline-block p-3 text-left ${
                        isOwn
                          ? 'border-transparent bg-primary text-primary-foreground'
                          : 'border-border bg-card'
                      }`}
                    >
                      <p className="whitespace-pre-wrap text-sm">{msg.body}</p>
                    </Card>
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      {format(new Date(msg.created_at), 'HH:mm', { locale: sv })}
                    </p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Svarsfält — fast ovanför den flytande bottenmenyn */}
      <div
        className="fixed left-1/2 z-40 w-[calc(100vw-1.25rem)] max-w-[420px] -translate-x-1/2"
        style={{ bottom: 'calc(env(safe-area-inset-bottom) + 5rem)' }}
      >
        <form
          onSubmit={handleSend}
          className="flex gap-2 rounded-3xl border border-border bg-card/80 p-2 backdrop-blur-xl"
        >
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Svara kunden..."
            rows={1}
            maxLength={MAX_MESSAGE_LENGTH}
            className="max-h-32 min-h-[44px] flex-1 resize-none rounded-2xl border-none bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend(e);
              }
            }}
          />
          <Button
            type="submit"
            size="icon"
            className="shrink-0 self-end rounded-full"
            disabled={!message.trim() || sendMutation.isPending}
          >
            {sendMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
